import axios from 'axios';
import _ from 'lodash';
import store from 'app/store';
import { getProducersQuery, getLastPickupsQuery, getHaulingCompaniesQuery, getDriversQuery } from './queries';
import { buildExpressQuery, buildLoopbackQuery } from './queries/builders';
import { getTimeSinceLastPickup, getBulkQueryData } from './utils';
import { arrayToMap, resolveId } from '../../utils';
import { requiresProducerBusinessData } from './utils/regionUtils';
import Split from '../split';

const getProducerListDetails = ({ filterDeleted = true, filterInactive, includePickups, filterSplitProducer, fields } = {}) =>
    new Promise((resolve, reject) => {
        const { region } = store.getState().persisted.auth.user.data;
        const queryBuilder = Split.getBooleanTreatment('use_express_auth_endpoints') ? buildExpressQuery : buildLoopbackQuery;
        const queryProducers = getProducersQuery({
            filterDeleted,
            filterInactive,
            filterSplitProducer,
            fields,
            queryBuilder,
        });

        const queryHaulingCompanies = getHaulingCompaniesQuery({ fields: ['id', 'full_name', 'hauler_number', 'name'], queryBuilder });

        const queries = [axios.get(queryProducers), axios.get(queryHaulingCompanies)];

        if (includePickups) {
            const queryPickups = getLastPickupsQuery(queryBuilder);
            queries.push(axios.get(queryPickups));
            const queryDrivers = getDriversQuery({ fields: ['id', 'hauling_id'], queryBuilder });
            queries.push(axios.get(queryDrivers));
        }

        Promise.all(queries)
            .then(async (response) => {
                const producersRaw = Array.isArray(response[0].data) ? response[0].data.filter(Boolean) : [];
                let producers = _.orderBy(producersRaw.map(resolveId), ['name'], ['asc']);
                const haulersRaw = Array.isArray(response[1].data) ? response[1].data.filter(Boolean) : [];
                const haulers = _.reduce(
                    haulersRaw.map(resolveId),
                    (map, hauler) => {
                        return { ...map, [hauler.id]: hauler };
                    },
                    {}
                );

                let pickups = {};
                let drivers = {};

                // Only get producer business data if required for this region
                const needsProducerBusinessData = requiresProducerBusinessData(region);

                if (needsProducerBusinessData) {
                    // Extract producer business IDs from the producers data
                    const producerBusinessIds = _.filter(_.uniq(_.map(producers, 'producer_business')), (id) => id != null);

                    if (producerBusinessIds.length > 0) {
                        // Fetch producer business details
                        const producerBusinesses = await getBulkQueryData(getProducersQuery, {
                            fields: ['id', 'license_number', 'name'],
                            ids: producerBusinessIds,
                        });

                        // Create a map of producer businesses by ID
                        const producerBusinessMap = arrayToMap(producerBusinesses, 'id');

                        // Add producer business details to each producer
                        producers = producers.map((producer) => {
                            const producerBusinessId = producer.producer_business;
                            if (producerBusinessId) {
                                const producerBusiness = producerBusinessMap[producerBusinessId];
                                if (producerBusiness) {
                                    return {
                                        ...producer,
                                        producer_business_name: producerBusiness.name || '',
                                        producer_business_license_number: producerBusiness.license_number || '',
                                    };
                                }
                            }
                            return producer;
                        });
                    }
                }

                if (includePickups) {
                    const pickupsRaw = Array.isArray(response[2].data) ? response[2].data.filter(Boolean) : [];
                    pickups = _.reduce(
                        pickupsRaw.map(resolveId),
                        (map, pickup) => {
                            return { ...map, [pickup.producer_id]: pickup };
                        },
                        {}
                    );

                    const driversRaw = Array.isArray(response[3].data) ? response[3].data.filter(Boolean) : [];
                    drivers = _.reduce(
                        driversRaw.map(resolveId),
                        (map, driver) => {
                            return { ...map, [driver.id]: driver };
                        },
                        {}
                    );
                }

                producers = _.map(producers, (producer) => {
                    const p = { ...producer };

                    if (includePickups && pickups[producer.id]) {
                        p.pickup_date = pickups[producer.id].created_at;
                        p.pickup_days = getTimeSinceLastPickup(pickups[producer.id]);

                        const driver = drivers[pickups[producer?.id]?.driver_id];
                        p.hauler_num = haulers[driver?.hauling_id]?.hauler_number;
                        p.hauler_name = haulers[driver?.hauling_id]?.full_name;
                    }

                    if (p.default_hauling_company && haulers[p.default_hauling_company]) {
                        p.default_hauler_name = haulers[p.default_hauling_company]?.name || '-';
                    }

                    return p;
                });

                resolve({ producers });
            })

            .catch((errors) => {
                // eslint-disable-next-line no-console
                console.log(errors);
                reject(new Error('Unable to process request'));
            });
    });

export default getProducerListDetails;
